import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoCameraIcon, XMarkIcon, FilmIcon } from '@heroicons/react/24/outline';
import { useDialog } from '../../hooks/useDialog';
import { IUserModel, IVideoGeneration } from '@packages/global';
import VideoSelector from './VideoSelector';
import { useOssDirectUpload } from '../../hooks/useOssDirectUpload';

interface VideoUploadProps {
  value?: string;
  onChange: (url: string) => void;
  placeholder?: string;
  required?: boolean;
  models?: IUserModel[];
}

export default function VideoUpload({ value, onChange, placeholder, required, models = [] }: VideoUploadProps) {
  const { t } = useTranslation(['common_ui', 'common']);
  const { uploadFile, uploading } = useOssDirectUpload();
  const [dragOver, setDragOver] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const { alert } = useDialog();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadVideo = async (file: File) => {
    // 文件类型预验证
    const allowedVideoTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm'];
    if (!allowedVideoTypes.includes(file.type)) {
      await alert(t('common_ui:upload.video.invalidType'), t('common_ui:upload.video.uploadFailedTitle'), 'danger');
      return;
    }
    // 文件大小验证（200MB）
    if (file.size > 200 * 1024 * 1024) {
      await alert(t('common_ui:upload.video.sizeLimit'), t('common_ui:upload.video.uploadFailedTitle'), 'danger');
      return;
    }

    try {
      const url = await uploadFile(file);
      onChange(url);
    } catch (error) {
      console.error('视频上传失败:', error);
      await alert(t('common_ui:upload.video.uploadFailed'), t('common_ui:upload.video.uploadFailedTitle'), 'danger');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadVideo(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    const allowedVideoTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm'];
    if (file && allowedVideoTypes.includes(file.type)) {
      uploadVideo(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const clearVideo = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleVideoSelect = (video: IVideoGeneration) => {
    if (video.resultVideoUrl) {
      onChange(video.resultVideoUrl);
      setShowSelector(false);
    }
  };

  return (
    <div className="space-y-2">
      {value ? (
        <div className="bg-bg-elevated border border-border-subtle rounded-md p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1">
              <VideoCameraIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <video
                controls
                className="w-full h-32 rounded border border-border-subtle"
                preload="metadata"
              >
                <source src={value} type="video/mp4" />
                <source src={value} type="video/mov" />
                <source src={value} type="video/avi" />
                {t('common_ui:upload.video.unsupported')}
              </video>
            </div>
            <button
              type="button"
              onClick={clearVideo}
              className="text-gray-400 hover:text-gray-200 ml-2 flex-shrink-0"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className={`border-2 border-dashed rounded-md p-4 ${dragOver
          ? 'border-primary bg-primary/10'
          : 'border-border-default hover:border-border-default'
          }`}>
          <div
            className={`aspect-video  text-center cursor-pointer transition-colors `}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileSelect}
              className="hidden"
              required={required && !value}
            />

            {uploading ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mb-2"></div>
                <p className="text-sm text-gray-400">{t('common_ui:upload.video.uploading')}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <VideoCameraIcon className="h-8 w-8 text-gray-400 mb-3" />
                <p className="text-sm text-gray-300 mb-1">
                  {t('common_ui:upload.video.dragHint')}
                </p>                <p className="text-xs text-gray-500">
                  {t('common_ui:upload.video.hint')}
                </p>
              </div>
            )}
          </div>
          <div className='flex items-center justify-center gap-2 mt-2'>
            <button
              type="button"
              className='px-2 py-1 bg-blue-700/50 text-white rounded-md hover:bg-blue-600/50 transition-colors flex items-center gap-1'
              onClick={() => setShowSelector(true)}
              disabled={uploading}
            >
              <FilmIcon className="w-3 h-3" />
              <span className="text-xs">{t('common_ui:upload.video.videoLibrary')}</span>
            </button>
          </div>
        </div>
      )}

      {placeholder && !value && (
        <p className="text-xs text-gray-500">{placeholder}</p>
      )}


      <VideoSelector
        isOpen={showSelector}
        onClose={() => setShowSelector(false)}
        onSelect={handleVideoSelect}
        models={models}
      />
    </div>
  );
}